import { PolicyDialog } from '@communities/content-management/components';
import type { Policy } from '@communities/content-management/domain';
import { useCommunityPolicies, useDeleteCommunityPolicy } from '@communities/content-management/hooks';
import { DataList, type DeleteItemContext, type TableColumn } from '@core/components';
import { SectionHeader } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Button, Badge, IconButton, Info16, IconButtonSize, IconSize, Section } from '@soteria/design-system-ve';
import { useState, useCallback } from 'react';

interface PoliciesTabProps {
  communityId: string;
  isEntityAdmin: boolean;
}

function PoliciesTabComponent({ communityId, isEntityAdmin }: PoliciesTabProps) {
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | undefined>(undefined);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { policies, loading, errors } = useCommunityPolicies(communityId);
  const { deletePolicy, loading: isDeleting, errors: deleteErrors, resetErrors } = useDeleteCommunityPolicy(communityId);

  const columns: TableColumn<Policy>[] = [
    {
      key: 'name',
      header: 'Name',
      render: policy => policy.name,
      width: '2fr',
    },
    {
      key: 'description',
      header: 'Description',
      render: policy => policy.description || '-',
      width: '3fr',
    },
    {
      key: 'status',
      header: 'Status',
      render: policy => <Badge text={policy.isEnabled ? 'Enabled' : 'Disabled'} />,
      width: '1fr',
    },
    {
      key: 'labels',
      header: 'Labels',
      render: policy => (
        <span>
          {policy.labels.length} {policy.labels.length === 1 ? 'label' : 'labels'}
        </span>
      ),
      width: '1fr',
    },
  ];

  const handleOpenDialog = useCallback((policy?: Policy) => {
    setSelectedPolicy(policy || undefined);
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setSelectedPolicy(undefined);
  }, []);

  // Create the delete item context
  const deleteItemContext: DeleteItemContext<Policy> | undefined = isEntityAdmin
    ? {
        handler: async (item: Policy) => await deletePolicy(communityId, item.id),
        isDeleting,
        errors: deleteErrors,
        resetErrors,
      }
    : undefined;

  return (
    <>
      <Section spacing="large">
        <SectionHeader
          description="Configure policies for grouping content by labels and severity thresholds."
          endComponent={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <IconButton
                aria-label="Learn more about policies"
                buttonSize={IconButtonSize.Medium}
                icon={Info16}
                iconSize={IconSize.XSmall}
                onClick={() => {
                  /* TODO: Add more info dialog */
                }}
              />
              {isEntityAdmin && <Button onClick={() => handleOpenDialog()}>ADD POLICY</Button>}
            </div>
          }
          title={`Policies (${policies.length})`}
        />

        {/* Policies List */}
        <DataList
          columns={columns}
          data={policies}
          dataTypeName="policy"
          deleteItemContext={deleteItemContext}
          error={errors}
          isLoading={loading}
          onEdit={isEntityAdmin ? handleOpenDialog : undefined}
        />
      </Section>

      {/* Create/Edit Policy Dialog */}
      <PolicyDialog communityId={communityId} isOpen={isDialogOpen} policy={selectedPolicy} onClose={handleCloseDialog} />
    </>
  );
}

export const PoliciesTab = withAppInsightsTracking(PoliciesTabComponent, 'PoliciesTab');
